import dayjs from 'dayjs'
import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { ELIGIBILITY_STATUS } from 'features/libra/types/eligibility'
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import {
  getComputedEligibility,
  NiiReportFields,
  useNiiLocalFieldValues,
} from './niiReportApi'

interface NiiReportDataContextValue {
  localValues: FieldValues
  setLocalFieldValue: (key: string, value: FieldValue) => void
  hasChanges: boolean
  doSave: () => Promise<void>
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
}

const NiiReportDataContext = createContext<NiiReportDataContextValue | null>(
  null
)

export const NiiReportDataProvider: FC<
  PropsWithChildren<{ fields: NiiReportFields }>
> = ({ children, fields }) => {
  const { fieldValues: ticketFieldValues } = useTicketAccess()

  const defaults = useMemo<FieldValues>(() => {
    const computed = getComputedEligibility(
      ticketFieldValues,
      fields.eligibilityComputed
    )
    const result: FieldValues = {}

    if (!ticketFieldValues[fields.analysisDate]) {
      result[fields.analysisDate] = wrapFieldValue(dayjs().format('YYYY-MM-DD'))
    }

    if (computed !== ELIGIBILITY_STATUS.UNCLEAR) {
      result[fields.eligibility] = wrapFieldValue(computed)
    }

    return result
  }, [
    ticketFieldValues,
    fields.eligibilityComputed,
    fields.eligibility,
    fields.analysisDate,
  ])

  const value = useNiiLocalFieldValues(fields, defaults)
  return (
    <NiiReportDataContext.Provider value={value}>
      {children}
    </NiiReportDataContext.Provider>
  )
}

export const useNiiReportData = (): NiiReportDataContextValue => {
  const ctx = useContext(NiiReportDataContext)
  if (!ctx)
    throw new Error(
      'useNiiReportData must be used within NiiReportDataProvider'
    )
  return ctx
}
